import { useEffect, useMemo, useRef, useState } from "react"

import { closesFence, getFenceMatch, splitFenceLines } from "@/lib/fence-tracking"

/**
 * Target number of animation frames over which to drain the reveal buffer.
 * At 60 fps this means ~200 ms of smooth character-by-character reveal.
 */
const DRAIN_FRAMES = 12

/**
 * Identifies regions in the parsed HTML string that must be revealed as
 * complete units to prevent broken rendering. Returns sorted, non-overlapping
 * [start, end) index pairs.
 *
 * Atomic regions:
 * - HTML elements:   <tag ...>...</tag>  (e.g. citation buttons)
 * - Display math:    $$...$$
 * - Inline math:     $...$  (same line, not escaped)
 * - Markdown images: ![alt](url)
 * - Markdown links:  [text](url)
 */
/**
 * Find the matching closing tag for a given tag name, handling nested same-name tags.
 * Returns the index of the closing tag, or -1 if not found.
 */
function findMatchingClose(html: string, tagName: string, startPos: number): number {
  const closingTag = `</${tagName}>`
  const openTag = `<${tagName}`
  let depth = 1
  let searchPos = startPos

  while (depth > 0 && searchPos < html.length) {
    const nextClose = html.indexOf(closingTag, searchPos)
    if (nextClose === -1) return -1
    // Count nested opens between searchPos and nextClose
    let openPos = searchPos
    while (openPos < nextClose) {
      const idx = html.indexOf(openTag, openPos)
      if (idx === -1 || idx >= nextClose) break
      const after = html[idx + openTag.length]
      if (after === " " || after === ">" || after === "/" || after === "\t") depth++
      openPos = idx + openTag.length
    }
    depth--
    if (depth === 0) return nextClose
    searchPos = nextClose + closingTag.length
  }

  return -1
}

// `mermaid` as a word, so the streaming rename `mermaid_pending` is not atomic.
// Indent and the marker match the previous opener: 0–3 spaces, then ``` or ~~~.
function mermaidOpenerMarker(line: string): string | null {
  const match = getFenceMatch(line)
  if (!match) return null
  const [, indent, marker, spacing, info] = match
  if (!/^ {0,3}$/.test(indent) || !/^[ \t]*$/.test(spacing) || !/^mermaid\b/i.test(info)) {
    return null
  }
  return marker
}

/**
 * End of the closed Mermaid fence opening at `start`, or -1. A diagram is only
 * renderable whole: revealing its code a few characters at a time hands
 * MermaidBlock a new, incomplete diagram every frame (#3485).
 *
 * Line endings and the closer come from `splitFenceLines` / `closesFence`, so
 * LF, CRLF, and bare CR follow the chat fence parser. The closer's own line
 * ending stays outside the span.
 */
function findClosedMermaidFenceEnd(html: string, start: number): number {
  const lines = splitFenceLines(html.slice(start))
  const opener = lines[0]
  const marker = mermaidOpenerMarker(opener.content)
  // A closer is a later line, so an opener that never ends is not atomic.
  if (!opener.separator || !marker) return -1

  const open = { lineIndex: 0, marker }
  let offset = opener.content.length + opener.separator.length

  for (let lineIndex = 1; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex]
    const closer = getFenceMatch(line.content)
    if (closer && closesFence(open, line.content, closer)) {
      return start + offset + line.content.length
    }
    offset += line.content.length + line.separator.length
  }

  return -1
}

export function computeAtomicSpans(html: string): [number, number][] {
  const spans: [number, number][] = []
  let i = 0

  while (i < html.length) {
    const ch = html[i]

    // ── Closed Mermaid fence: revealed whole ──
    // `\r` is a line start too: a bare-CR fence is never preceded by `\n`.
    if (
      (ch === "`" || ch === "~" || ch === " ") &&
      (i === 0 || html[i - 1] === "\n" || html[i - 1] === "\r")
    ) {
      const fenceEnd = findClosedMermaidFenceEnd(html, i)
      if (fenceEnd !== -1) {
        spans.push([i, fenceEnd])
        i = fenceEnd
        continue
      }
    }

    // ── HTML element: <tagName …>…</tagName> ──
    if (ch === "<" && i + 1 < html.length && /[a-zA-Z]/.test(html[i + 1])) {
      let j = i + 1
      while (j < html.length && /[a-zA-Z0-9]/.test(html[j])) j++
      const tagName = html.substring(i + 1, j)
      const closeIdx = findMatchingClose(html, tagName, j)

      if (closeIdx !== -1) {
        const end = closeIdx + `</${tagName}>`.length
        spans.push([i, end])
        i = end
        continue
      }
      // Self-closing or unclosed — skip past >
      const gt = html.indexOf(">", j)
      if (gt !== -1) {
        spans.push([i, gt + 1])
        i = gt + 1
        continue
      }
    }

    // ── Display math: $$...$$ ──
    if (ch === "$" && html[i + 1] === "$") {
      const close = html.indexOf("$$", i + 2)
      if (close !== -1) {
        const end = close + 2
        spans.push([i, end])
        i = end
        continue
      }
    }

    // ── Inline math: $...$ (same line, not $$, not escaped) ──
    if (
      ch === "$" &&
      (i === 0 || html[i - 1] !== "\\") &&
      (i + 1 >= html.length || html[i + 1] !== "$")
    ) {
      let found = false
      for (let j = i + 1; j < html.length && html[j] !== "\n"; j++) {
        if (
          html[j] === "$" &&
          html[j - 1] !== "\\" &&
          (j + 1 >= html.length || html[j + 1] !== "$")
        ) {
          spans.push([i, j + 1])
          i = j + 1
          found = true
          break
        }
      }
      if (found) continue
    }

    // Note: \[...\] and \(...\) are converted to $$/$  by the answer parser
    // before reaching this hook, so only dollar-sign math needs detection.

    // ── Markdown image: ![alt](url) ──
    if (ch === "!" && html[i + 1] === "[") {
      const closeBracket = html.indexOf("]", i + 2)
      if (closeBracket !== -1 && html[closeBracket + 1] === "(") {
        const closeParen = html.indexOf(")", closeBracket + 2)
        if (closeParen !== -1) {
          spans.push([i, closeParen + 1])
          i = closeParen + 1
          continue
        }
      }
    }

    // ── Markdown link: [text](url) ──
    if (ch === "[" && (i === 0 || html[i - 1] !== "!")) {
      const closeBracket = html.indexOf("]", i + 1)
      if (closeBracket !== -1 && html[closeBracket + 1] === "(") {
        const closeParen = html.indexOf(")", closeBracket + 2)
        if (closeParen !== -1) {
          spans.push([i, closeParen + 1])
          i = closeParen + 1
          continue
        }
      }
    }

    i++
  }

  return spans
}

/**
 * If pos falls inside an atomic span, snap to its end so the entire region
 * is revealed as a complete unit. Otherwise return pos unchanged.
 */
function snapToSafeBoundary(pos: number, spans: ReadonlyArray<[number, number]>): number {
  for (const [start, end] of spans) {
    if (start >= pos) return pos // past all relevant spans
    if (pos > start && pos < end) return end
  }
  return pos
}

/**
 * Smoothly reveals streaming content character-by-character using
 * requestAnimationFrame, creating a typewriter effect.
 *
 * Atomic regions (HTML elements, LaTeX math, citations) are revealed as
 * complete units to prevent broken rendering mid-tag or mid-expression.
 *
 * During streaming the hook maintains an internal "revealed length" that
 * advances toward the full content string each frame.  When streaming
 * stops the full content is shown immediately.
 */
export function useTypewriterReveal(
  content: string,
  isStreaming: boolean,
  { plainText = false }: { plainText?: boolean } = {}
): string {
  const targetRef = useRef(content)
  const revealedLenRef = useRef(content.length)
  const rafRef = useRef(0)
  const [revealed, setRevealed] = useState(content)

  // Pre-compute atomic spans whenever content changes
  // Plain text (a spoken caption) has no markup to keep whole: "$5 to $10" is
  // two prices, not inline math, and must type in like any other words.
  const atomicSpans = useMemo(
    () => (plainText ? [] : computeAtomicSpans(content)),
    [content, plainText]
  )
  const spansRef = useRef(atomicSpans)
  spansRef.current = atomicSpans

  // Keep target in sync without triggering effects
  targetRef.current = content

  // When not streaming, immediately sync revealed to full content
  useEffect(() => {
    if (!isStreaming) {
      revealedLenRef.current = content.length
      setRevealed(content)
    }
  }, [content, isStreaming])

  // Plain text can be revised, not only extended (a recogniser changing its
  // mind). What was typed so far is then no longer a prefix: start over from
  // the revision in full instead of cutting it at the old length and retyping.
  const revealedRef = useRef(revealed)
  revealedRef.current = revealed
  useEffect(() => {
    if (!plainText || !isStreaming || content.startsWith(revealedRef.current)) return
    revealedLenRef.current = content.length
    setRevealed(content)
  }, [content, isStreaming, plainText])

  // While streaming, run a rAF loop that gradually reveals characters
  useEffect(() => {
    if (!isStreaming) {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = 0
      }
      return
    }

    const tick = () => {
      const target = targetRef.current
      const currentLen = revealedLenRef.current

      if (currentLen < target.length) {
        const remaining = target.length - currentLen
        const chars = Math.max(1, Math.ceil(remaining / DRAIN_FRAMES))
        let nextLen = Math.min(currentLen + chars, target.length)

        // Snap to safe boundary so we never slice inside HTML/math
        nextLen = snapToSafeBoundary(nextLen, spansRef.current)
        // Never go backwards
        nextLen = Math.max(nextLen, currentLen)

        revealedLenRef.current = nextLen
        setRevealed(target.substring(0, nextLen))
        rafRef.current = requestAnimationFrame(tick)
        return
      }

      rafRef.current = 0
    }

    // A hidden tab never runs animation frames, so the reveal would stall on
    // the last visible prefix. Show everything at once instead (issue #3354).
    if (typeof document !== "undefined" && document.hidden) {
      revealedLenRef.current = targetRef.current.length
      setRevealed(targetRef.current)
      return
    }

    if (!rafRef.current && revealedLenRef.current < targetRef.current.length) {
      rafRef.current = requestAnimationFrame(tick)
    }

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = 0
      }
    }
  }, [content, isStreaming])

  return revealed
}
